import { Outlet, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StoreHeader from "../components/store/StoreHeader";
import Footer from "../components/store/Footer";
import HalloweenSplash from "../components/store/HalloweenSplash";
import "./StoreLayout.css";

function parseStoreFilterIdsFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const norm = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const ofertaRaw = String(params.get("oferta") ?? params.get("ofertas") ?? "")
    .trim()
    .toLowerCase();
  const destacadosRaw = String(
    params.get("destacados") ?? params.get("destacado") ?? ""
  )
    .trim()
    .toLowerCase();
  return {
    category: norm(params.get("category")),
    brand: norm(params.get("brand")),
    subcategory: norm(params.get("subcategory")),
    oferta: ofertaRaw === "1" || ofertaRaw === "true",
    destacados: destacadosRaw === "1" || destacadosRaw === "true",
  };
}

function StoreLayout() {
  const layoutRef = useRef(null);
  const location = useLocation();

  const [search, setSearch] = useState("");

  // Pantalla de carga: se mantiene hasta que el branding real esté resuelto,
  // para no mostrar la configuración por defecto en conexiones lentas.
  const [brandingReady, setBrandingReady] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [storeName, setStoreName] = useState("");

  const handleHeaderReady = useCallback(({ nombreTienda } = {}) => {
    setStoreName(nombreTienda || "");
    setBrandingReady(true);
  }, []);

  // El splash avisa cuando terminó su animación de salida.
  const handleSplashDone = useCallback(() => setShowSplash(false), []);

  // Salvaguarda: si el branding nunca responde, no dejamos el splash colgado.
  useEffect(() => {
    const t = setTimeout(() => setBrandingReady(true), 10000);
    return () => clearTimeout(t);
  }, []);

  const { category, brand, subcategory, oferta, destacados } = useMemo(
    () => parseStoreFilterIdsFromSearch(location.search),
    [location.search]
  );

  useEffect(() => {
    const scroller =
      document.querySelector(".store-layout") ||
      document.querySelector(".store-container");

    if (scroller) scroller.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const layoutEl = layoutRef.current;
    if (!layoutEl) return;

    const headerFixedEl = layoutEl.querySelector(".store-header-fixed");
    if (!headerFixedEl) return;

    const applyHeaderOffset = () => {
      const h = headerFixedEl.getBoundingClientRect().height;
      layoutEl.style.setProperty("--store-header-h", `${Math.ceil(h)}px`);
    };

    applyHeaderOffset();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", applyHeaderOffset);
      return () => window.removeEventListener("resize", applyHeaderOffset);
    }

    const ro = new ResizeObserver(() => applyHeaderOffset());
    ro.observe(headerFixedEl);

    return () => ro.disconnect();
  }, []);

  return (
    <div ref={layoutRef} className="jwl-scope store-layout">
      {showSplash && (
        <HalloweenSplash
          ready={brandingReady}
          storeName={storeName}
          onDone={handleSplashDone}
        />
      )}

      <div className="store-header-fixed">
        <StoreHeader onSearch={setSearch} onReady={handleHeaderReady} />
      </div>

      <div className="store-content store-content--full">
        <main className="store-container">
          <Outlet
            context={{ search, category, brand, subcategory, oferta, destacados }}
          />
        </main>
      </div>

      <Footer />
    </div>
  );
}

export default StoreLayout;
